import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const backpropagationComponent: ComponentDef = {
  id: 'backpropagation',
  name: 'Backpropagation',
  description: 'Chain-rule gradient flow: forward pass computes predictions and loss; backward pass propagates ∂L/∂W, ∂L/∂x, ∂L/∂b through each layer.',
  category: 'function',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'num_classes', label: 'Classes', type: 'number', default: 1000, min: 2, max: 100000, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'computation_graph',
      options: [
        { label: 'Computation Graph', value: 'computation_graph' },
        { label: 'Chain Rule', value: 'chain_rule' },
        { label: 'Memory Analysis', value: 'memory_analysis' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const num_classes = Number(params.num_classes);
    const variant = String(params.variant || 'computation_graph');

    const g = new GraphBuilder();

    if (variant === 'computation_graph') {
      // ── COMPUTATION GRAPH: forward DAG + backward gradient flow ──

      // Forward pass nodes (DAG: x -> W1 -> act -> W2 -> softmax -> loss)
      g.input('x', [seq_len, d_model]);
      g.input('targets', [seq_len]);

      g.linear('fwd_W1', 'x', [d_model, d_model]);
      g.activation('fwd_act', 'fwd_W1', 'GELU');
      g.linear('fwd_W2', 'fwd_act', [d_model, num_classes]);
      g.softmax('fwd_probs', 'fwd_W2', -1);
      g.crossEntropy('fwd_loss', 'fwd_probs', 'targets');

      // Backward pass: gradient flow in reverse order
      g.activation('bwd_delta_loss', 'fwd_loss', '∂L/∂logits = p − y');

      // Gradient w.r.t. W2
      g.transpose('bwd_act_T', 'fwd_act', [d_model, seq_len], 'act^T');
      g.matmul('bwd_grad_W2', 'bwd_act_T', 'bwd_delta_loss', [d_model, num_classes]);

      // Gradient w.r.t. act (backprop through W2)
      g.activation('bwd_W2_T', 'fwd_W2', 'W2^T');
      g.matmul('bwd_delta_act', 'bwd_delta_loss', 'bwd_W2_T', [seq_len, d_model]);

      // Gradient through activation
      g.activation('bwd_gelu_prime', 'fwd_W1', "GELU'");
      g.mul('bwd_delta_W1x', 'bwd_delta_act', 'bwd_gelu_prime');

      // Gradient w.r.t. W1
      g.transpose('bwd_x_T', 'x', [d_model, seq_len], 'x^T');
      g.matmul('bwd_grad_W1', 'bwd_x_T', 'bwd_delta_W1x', [d_model, d_model]);

      // Gradient w.r.t. x
      g.activation('bwd_W1_T', 'fwd_W1', 'W1^T');
      g.matmul('bwd_grad_x', 'bwd_delta_W1x', 'bwd_W1_T', [seq_len, d_model]);

      g.output('out', 'bwd_grad_W1');

    } else if (variant === 'chain_rule') {
      // ── CHAIN RULE: explicit partial derivative decomposition ──
      // dL/dW1 = dL/dy * dy/dh * dh/dW1

      // Show each partial derivative as a node
      g.input('x', [seq_len, d_model]);
      g.input('targets', [seq_len]);

      // Forward pass (compact)
      g.linear('h', 'x', [d_model, d_model]);
      g.activation('act_h', 'h', 'GELU(h)');
      g.linear('y', 'act_h', [d_model, num_classes]);
      g.softmax('probs', 'y', -1);
      g.crossEntropy('loss', 'probs', 'targets');

      // Chain rule decomposition: dL/dW1 = dL/dy * dy/dh * dh/dW1
      // Term 1: dL/dy (gradient at output)
      g.activation('dL_dy', 'loss', 'dL/dy = probs − one_hot(y)');

      // Term 2: dy/dh = W2^T (Jacobian of linear layer)
      g.activation('dy_dh', 'y', 'dy/dh = W2^T');

      // Term 3: dh/dW1 = x^T * GELU'(h) (gradient through activation + linear)
      g.activation('dh_dW1_act', 'h', "dh/dh_pre = GELU'(h)");
      g.transpose('x_T', 'x', [d_model, seq_len], 'x^T');
      g.activation('dh_dW1', 'x_T', 'dh/dW1 = x^T');

      // Compose: dL/dh = dL/dy * dy/dh
      g.matmul('dL_dh', 'dL_dy', 'dy_dh', [seq_len, d_model]);

      // Apply activation derivative: dL/dh_pre = dL/dh * GELU'(h)
      g.mul('dL_dh_pre', 'dL_dh', 'dh_dW1_act');

      // Final: dL/dW1 = dL/dh_pre * dh/dW1 (via x^T)
      g.transpose('dL_dh_pre_T', 'dL_dh_pre', [d_model, seq_len], '(dL/dh_pre)^T');
      g.matmul('dL_dW1', 'dL_dh_pre_T', 'x_T', [d_model, d_model]);

      // Also show dL/dW2 for completeness
      g.transpose('act_h_T', 'act_h', [d_model, seq_len], 'GELU(h)^T');
      g.matmul('dL_dW2', 'act_h_T', 'dL_dy', [d_model, num_classes]);

      g.output('out', 'dL_dW1');

    } else {
      // ── MEMORY ANALYSIS: activation, gradient, and optimizer memory ──

      // Forward pass: track activation memory
      g.input('x', [seq_len, d_model]);
      g.input('targets', [seq_len]);

      // Layer 1 forward
      g.linear('fwd_W1', 'x', [d_model, d_model]);
      g.activation('fwd_act', 'fwd_W1', 'GELU');

      // Layer 2 forward
      g.linear('fwd_W2', 'fwd_act', [d_model, num_classes]);
      g.softmax('fwd_probs', 'fwd_W2', -1);
      g.crossEntropy('fwd_loss', 'fwd_probs', 'targets');

      // Activation memory: stored for backward pass
      g.scale('mem_x', 'x', 'Activation mem: x');
      g.scale('mem_W1', 'fwd_W1', 'Activation mem: W1·x');
      g.scale('mem_act', 'fwd_act', 'Activation mem: GELU(W1·x)');
      g.scale('mem_W2', 'fwd_W2', 'Activation mem: W2·h');

      // Total forward activation memory
      g.concat('fwd_mem_total', ['mem_x', 'mem_W1', 'mem_act', 'mem_W2'], [seq_len, d_model * 4]);
      g.scale('fwd_mem_label', 'fwd_mem_total', '4× activation memory (forward)');

      // Backward pass: gradient memory
      g.activation('bwd_delta', 'fwd_loss', '∂L/∂logits');
      g.scale('mem_grad_logits', 'bwd_delta', 'Grad mem: ∂L/∂logits');

      g.transpose('act_T', 'fwd_act', [d_model, seq_len], 'act^T');
      g.matmul('grad_W2', 'act_T', 'bwd_delta', [d_model, num_classes]);
      g.scale('mem_grad_W2', 'grad_W2', 'Grad mem: ∂L/∂W2');

      g.activation('W2_T', 'fwd_W2', 'W2^T');
      g.matmul('delta_act', 'bwd_delta', 'W2_T', [seq_len, d_model]);
      g.scale('mem_grad_act', 'delta_act', 'Grad mem: ∂L/∂act');

      g.activation('gelu_prime', 'fwd_W1', "GELU'");
      g.mul('delta_W1x', 'delta_act', 'gelu_prime');
      g.transpose('x_T', 'x', [d_model, seq_len], 'x^T');
      g.matmul('grad_W1', 'x_T', 'delta_W1x', [d_model, d_model]);
      g.scale('mem_grad_W1', 'grad_W1', 'Grad mem: ∂L/∂W1');

      // Total gradient memory
      g.concat('bwd_mem_total', ['mem_grad_logits', 'mem_grad_W2', 'mem_grad_act', 'mem_grad_W1'], [d_model, num_classes + d_model * 2 + seq_len]);
      g.scale('bwd_mem_label', 'bwd_mem_total', '2× memory (backward gradients)');

      // Optimizer state memory (Adam: momentum + variance = 2× params)
      g.scale('opt_mem_m1', 'grad_W1', 'Adam m (momentum)');
      g.scale('opt_mem_v1', 'grad_W1', 'Adam v (variance)');
      g.scale('opt_mem_m2', 'grad_W2', 'Adam m (momentum)');
      g.scale('opt_mem_v2', 'grad_W2', 'Adam v (variance)');
      g.concat('opt_mem_total', ['opt_mem_m1', 'opt_mem_v1', 'opt_mem_m2', 'opt_mem_v2'], [d_model, d_model * 2 + num_classes * 2]);
      g.scale('opt_mem_label', 'opt_mem_total', '4× param memory (Adam states)');

      // Activation checkpointing: recompute activations during backward
      // Show dropout to represent discarding intermediate activations
      g.dropout('ckpt_act', 'fwd_act', 1.0);
      g.scale('ckpt_savings', 'ckpt_act', 'Checkpointing: drop intermediate → save ~50% activation mem');

      g.output('out', 'ckpt_savings');
    }

    return g.build();
  }
};
